import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { usePillars } from "@/contexts/PillarsContext";
import { ArrowRight, Lightning } from "@phosphor-icons/react";
import CoverAnimation from "@/components/CoverAnimation";

/** Background style presets — keyed to settings.cover_background_style */
const BG_STYLES = {
  warm: {
    background: "radial-gradient(ellipse at top, rgba(255,180,80,0.40) 0%, transparent 55%), radial-gradient(ellipse at bottom right, rgba(204,255,0,0.30) 0%, transparent 50%), linear-gradient(180deg, #FAEBD2 0%, #E8DECF 60%, #DCDEE2 100%)",
  },
  cool: {
    background: "radial-gradient(ellipse at top, rgba(80,140,255,0.32) 0%, transparent 55%), radial-gradient(ellipse at bottom left, rgba(14,165,183,0.28) 0%, transparent 50%), linear-gradient(180deg, #D8E5F2 0%, #D0D9E2 60%, #DCDEE2 100%)",
  },
  forest: {
    background: "radial-gradient(ellipse at top, rgba(80,255,140,0.30) 0%, transparent 55%), radial-gradient(ellipse at bottom right, rgba(204,255,0,0.28) 0%, transparent 50%), linear-gradient(180deg, #DCE9D8 0%, #D2DFCE 60%, #DCDEE2 100%)",
  },
  sunset: {
    background: "radial-gradient(ellipse at top, rgba(255,90,140,0.36) 0%, transparent 55%), radial-gradient(ellipse at bottom right, rgba(255,180,60,0.36) 0%, transparent 50%), linear-gradient(180deg, #FBDCDC 0%, #F0CFCF 60%, #DCDEE2 100%)",
  },
  midnight: {
    background: "radial-gradient(ellipse at top, rgba(140,80,255,0.30) 0%, transparent 55%), radial-gradient(ellipse at bottom left, rgba(204,255,0,0.24) 0%, transparent 50%), linear-gradient(180deg, #E0DBEF 0%, #D6D1E5 60%, #DCDEE2 100%)",
  },
  minimal: {
    background: "linear-gradient(180deg, #EAECEF 0%, #DCDEE2 100%)",
  },
  granite: {
    backgroundColor: "#C1C5CB",
    backgroundImage:
      "radial-gradient(ellipse at 25% 12%, rgba(255,255,255,0.55), transparent 55%)," +
      "radial-gradient(ellipse at 78% 88%, rgba(108,114,124,0.38), transparent 55%)," +
      "radial-gradient(circle at 55% 45%, rgba(204,255,0,0.05), transparent 50%)," +
      "url(\"data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20width='220'%20height='220'%3E%3Cfilter%20id='g'%3E%3CfeTurbulence%20type='fractalNoise'%20baseFrequency='0.7'%20numOctaves='4'%20stitchTiles='stitch'/%3E%3CfeColorMatrix%20type='saturate'%20values='0'/%3E%3C/filter%3E%3Crect%20width='100%25'%20height='100%25'%20filter='url(%23g)'%20opacity='0.6'/%3E%3C/svg%3E\")," +
      "linear-gradient(160deg, #CDD1D7 0%, #B4B9C0 50%, #C7CBD1 100%)",
    backgroundSize: "auto, auto, auto, 220px 220px, auto",
  },
};

export default function WelcomePage() {
  const { settings, loading, settingsLoaded } = usePillars();
  const nav = useNavigate();

  if (loading) {
    return <div className="min-h-screen grid place-items-center text-[#000000]" data-testid="welcome-loading">Loading...</div>;
  }

  const style = settings.cover_background_url
    ? { backgroundImage: `linear-gradient(rgba(220,222,226,0.55), rgba(220,222,226,0.85)), url(${settings.cover_background_url})`, backgroundSize: "cover", backgroundPosition: "center" }
    : (BG_STYLES[settings.cover_background_style] || BG_STYLES.granite);

  const buttonLabel = settings.cover_button_label || "Log in";
  const logo = settings.logo_url;

  return (
    <div
      data-testid="welcome-page"
      className="min-h-screen w-full flex flex-col relative overflow-hidden"
      style={style}
    >
      <CoverAnimation kind={settings.cover_animation || "none"} />

      {/* subtle noise/grain overlay for depth */}
      <div
        aria-hidden
        style={{
          position: "absolute", inset: 0, pointerEvents: "none", zIndex: 2,
          backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.05'/></svg>\")",
          mixBlendMode: "overlay",
        }}
      />

      <main className="flex-1 flex items-center justify-center px-6 py-16 relative" style={{ zIndex: 3 }}>
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="max-w-2xl text-center flex flex-col items-center"
        >
          {/* Logo */}
          <motion.div
            initial={{ scale: 0.85, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.05 }}
            className="mb-10"
            data-testid="welcome-logo-wrap"
          >
            {logo ? (
              <img
                src={logo}
                alt="Logo"
                className="max-h-32 md:max-h-40 w-auto object-contain drop-shadow-[0_0_30px_rgba(204,255,0,0.18)]"
                data-testid="welcome-logo-img"
              />
            ) : !settingsLoaded ? (
              <div className="w-24 h-24 md:w-28 md:h-28" data-testid="welcome-logo-loading" />
            ) : (
              <div
                className="w-24 h-24 md:w-28 md:h-28 rounded-3xl grid place-items-center font-display font-black text-5xl md:text-6xl"
                style={{
                  background: "linear-gradient(135deg, #CCFF00 0%, #B8E600 100%)",
                  color: "#000000",
                  boxShadow: "0 0 60px rgba(204,255,0,0.25)",
                }}
                data-testid="welcome-logo-fallback"
              >E</div>
            )}
          </motion.div>

          {/* Title — three-line stacked layout */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="font-display font-black tracking-tight mb-8 flex flex-col items-center"
            style={{ color: "#000000", lineHeight: 0.9 }}
            data-testid="welcome-title"
          >
            <span className="text-3xl md:text-4xl">The</span>
            <span className="text-6xl sm:text-7xl md:text-8xl lg:text-9xl my-1 whitespace-nowrap">Self-Agency</span>
            <span className="text-3xl md:text-4xl">Project</span>
          </motion.div>

          {/* Mission */}
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.35 }}
            className="text-lg md:text-2xl tracking-wide mb-12 font-display whitespace-nowrap"
            style={{ color: "#000000", fontWeight: settings.cover_mission_bold ? 800 : 600 }}
            data-testid="welcome-mission"
          >
            {settings.cover_mission}
          </motion.p>

          {/* CTA */}
          <motion.button
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            onClick={() => nav("/login")}
            data-testid="welcome-login-button"
            className="group inline-flex items-center gap-3 px-10 py-4 rounded-full font-display font-bold text-lg md:text-xl transition-all"
            style={{
              background: "#CCFF00",
              color: "#000000",
              boxShadow: "0 10px 40px rgba(204,255,0,0.35), 0 0 0 1px rgba(204,255,0,0.6) inset",
              cursor: "pointer",
              border: "none",
            }}
          >
            <Lightning size={22} weight="fill" />
            {buttonLabel}
            <ArrowRight size={22} weight="bold" className="transition-transform group-hover:translate-x-1" />
          </motion.button>
        </motion.div>
      </main>

      {/* ---- Below the fold: conversion sections ---- */}
      <section className="relative px-6 pb-4" style={{ zIndex: 3 }} data-testid="welcome-benefits">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-display font-extrabold text-2xl md:text-3xl text-[#000000] mb-6">What you get</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              ["Real-world skills", "Confidence, personal safety, communication under pressure and emotional resilience — built lesson by lesson."],
              ["Learn at your pace", "Short video lessons with quick knowledge checks. Your progress is saved automatically, on any device."],
              ["Try before you buy", "The first 5 lessons are free — no payment needed to start."],
              ["Risk-free purchase", "Lifetime access, secure Stripe checkout, and a full refund within 30 days if it's not for you."],
            ].map(([t, d]) => (
              <div key={t} className="rounded-2xl border border-[#C4C8CE] bg-white/80 backdrop-blur-sm p-5">
                <div className="font-display font-bold text-[#000000] mb-1 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full inline-block" style={{ background: "#CCFF00", boxShadow: "0 0 0 3px rgba(204,255,0,0.3)" }} />{t}
                </div>
                <div className="text-sm text-[#000000]">{d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative px-6 py-10" style={{ zIndex: 3 }} data-testid="welcome-how">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-display font-extrabold text-2xl md:text-3xl text-[#000000] mb-6">How it works</h2>
          <div className="grid sm:grid-cols-3 gap-4">
            {[
              ["1", "Create your account", "Sign up in under a minute — no card needed."],
              ["2", "Start the free lesson", "See exactly how the course teaches before paying."],
              ["3", "Unlock everything", "One payment, lifetime access, learn on your schedule."],
            ].map(([n, t, d]) => (
              <div key={n} className="rounded-2xl border border-[#C4C8CE] bg-white/80 backdrop-blur-sm p-5">
                <div className="font-display font-black text-3xl mb-1" style={{ color: "#A3B800" }}>{n}</div>
                <div className="font-display font-bold text-[#000000] mb-1">{t}</div>
                <div className="text-sm text-[#000000]">{d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative px-6 pb-12" style={{ zIndex: 3 }} data-testid="welcome-faq">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-display font-extrabold text-2xl md:text-3xl text-[#000000] mb-6">Questions, answered</h2>
          <div className="flex flex-col gap-2">
            {[
              ["Do I need to pay to try it?", "No — the first 5 lessons of the course are completely free. Create an account and press \"Start for free\"."],
              ["What if it's not for me?", "Every purchase includes a 30-day full refund, one click from your catalog page. No emails, no questions."],
              ["How long do I keep access?", "Lifetime. Buy once, return whenever you want, on any device — phone, tablet or computer."],
              ["Is my payment secure?", "Yes — payments are processed by Stripe, the same processor used by millions of businesses. We never see your card details."],
              ["Can I use it like an app?", "Yes — after logging in, choose \"Install the app\" from the menu for a full-screen app experience."],
              ["Seeing a warning while installing the app?", "That warning comes from an out-of-date Chrome on your phone, not from this app. Fix in 2 minutes: 1) Update Google Chrome (and Android System WebView if listed). 2) If you already added the app, long-press its icon and uninstall it — your account is safe. 3) Open Chrome (not Samsung Internet), visit this site, then tap menu ⋮ → \"Install app\". The warning won't come back — and if it ever does, \"Install anyway\" is safe: the install wrapper is built and signed by Google itself."],
            ].map(([q, a]) => (
              <details key={q} className="rounded-xl border border-[#C4C8CE] bg-white/80 backdrop-blur-sm px-5 py-3 group">
                <summary className="font-display font-bold text-[#000000] cursor-pointer list-none flex items-center justify-between">
                  {q}<span className="text-[#A3B800] transition-transform group-open:rotate-45 text-xl leading-none">+</span>
                </summary>
                <p className="text-sm text-[#000000] mt-2">{a}</p>
              </details>
            ))}
          </div>
          <div className="text-center mt-10">
            <button onClick={() => nav("/login")} data-testid="welcome-bottom-cta"
              className="inline-flex items-center gap-2 px-10 py-4 rounded-full font-display font-bold text-lg border-0 cursor-pointer"
              style={{ background: "#CCFF00", color: "#000000", boxShadow: "0 10px 40px rgba(204,255,0,0.35)" }}>
              <Lightning size={20} weight="fill" /> {buttonLabel}
            </button>
          </div>
        </div>
      </section>

      <footer className="text-center py-6 text-xs uppercase tracking-[0.3em] relative" style={{ color: "rgba(31,41,55,0.45)", zIndex: 3 }}>
        Powered by The Self-Agency Project
      </footer>
    </div>
  );
}

export { BG_STYLES };
